import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, Button, Grid, LoadingSpinner, JobCard } from '../components/ui';

const Home = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useApi();
  const [newJobName, setNewJobName] = useState('');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [files, setFiles] = useState([]);
  const [sampleSize, setSampleSize] = useState(3);
  const [maxImages, setMaxImages] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const uploadSectionRef = useRef(null);

  // Helper function for reusable scroll logic
  const scrollToUpload = () => {
    if (uploadSectionRef.current) {
      uploadSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle initial selection (when the component first renders)
  useEffect(() => {
    if (selectedJobId) {
      // We use a tiny timeout to ensure the DOM element has 
      // been rendered by React before we try to scroll to it
      const timeout = setTimeout(scrollToUpload, 100);
      return () => clearTimeout(timeout);
    }
  }, [selectedJobId]);

  // Fetch all jobs
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await api.get('/api/jobs');
      return response.data;
    },
  });

  const jobs = jobsData?.jobs || [];

  // Poll for job updates when there are processing jobs
  useEffect(() => {
    const jobsList = jobsData?.jobs || [];
    const hasProcessingJobs = jobsList.some(job => job.status === 'processing');

    if (hasProcessingJobs) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries(['jobs']);
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [jobsData, queryClient]);

  // Adjust sample size when files change
  useEffect(() => {
    if (files.length > 0 && sampleSize > files.length) {
      setSampleSize(files.length);
    }
  }, [files.length, sampleSize]);

  // Create new job mutation
  const createJobMutation = useMutation({
    mutationFn: async (name) => {
      const response = await api.post('/api/jobs', { name });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['jobs']);
      setSelectedJobId(data.id);
      setNewJobName('');
    },
  });

  // Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: async (jobId) => {
      const response = await api.delete(`/api/jobs/${jobId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['jobs']);
      if (selectedJobId === deleteJobMutation.variables) {
        setSelectedJobId(null);
        setFiles([]);
      }
    },
  });

  // Upload and process images mutation
  const processImagesMutation = useMutation({
    mutationFn: async ({ jobId, files, sampleSize, maxImages }) => {
      // Step 1: Upload files
      const uploadFormData = new FormData();
      files.forEach((file) => {
        uploadFormData.append('files', file);
      });

      await api.post(`/api/jobs/${jobId}/upload`, uploadFormData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      // Step 2: Start processing
      const processResponse = await api.post(`/api/jobs/${jobId}/process`, {
        sample_size: sampleSize,
        max_images: maxImages || null,
      });

      return processResponse.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['jobs']);
      setUploadProgress(null);
      // Reset form
      setSelectedJobId(null);
      setFiles([]);
      setSampleSize(3);
      setMaxImages('');
    },
    onError: () => {
      setUploadProgress(null);
    },
  });

  const handleCreateJob = (e) => {
    e.preventDefault();
    if (newJobName.trim()) {
      createJobMutation.mutate(newJobName);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(droppedFiles);
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(prevFiles => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      // Adjust sample size if it exceeds the new number of files
      if (sampleSize > newFiles.length && newFiles.length > 0) {
        setSampleSize(newFiles.length);
      }
      return newFiles;
    });
  };

  const handleProcessImages = () => {
    if (!selectedJobId || files.length === 0) {
      return;
    }

    processImagesMutation.mutate({
      jobId: selectedJobId,
      files: files,
      sampleSize: parseInt(sampleSize) || 3,
      maxImages: maxImages ? parseInt(maxImages) : null,
    });
  };

  const handleDeleteJob = (jobId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this job? This will remove all associated data.')) {
      deleteJobMutation.mutate(jobId);
    }
  };


  if (jobsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (jobsError) {
    return (
      <div className="text-center text-danger dark:text-danger p-4">
        Error loading jobs: {jobsError.message}
      </div>
    );
  }

  return (
    <div className="space-y-8">

      <Grid cols={1} gap={6}>
        {/* Create New Job */}
        <Card>
          <h2 className="text-2xl font-semibold text-text-primary dark:text-text-primary-dark mb-4">
            Create New Job
          </h2>
          <form onSubmit={handleCreateJob} className="flex gap-4">
            <input
              type="text"
              value={newJobName}
              onChange={(e) => setNewJobName(e.target.value)}
              placeholder="Enter job name..."
              className="flex-1 px-4 py-2 border border-border-light dark:border rounded-lg focus:outline-none bg-background-light dark:bg-surface-dark text-text-primary-light dark:text-primary"
              disabled={createJobMutation.isPending}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={!newJobName.trim() || createJobMutation.isPending}
            >
              {createJobMutation.isPending ? 'Creating...' : 'Create Job'}
            </Button>
          </form>
          {createJobMutation.isError && (
            <p className="mt-2 text-sm text-danger dark:text-danger">
              Error creating job: {createJobMutation.error.message}
            </p>
          )}
        </Card>

        {/* Existing Jobs */}
        <Card>
          <h2 className="text-2xl font-semibold text-text-primary dark:text-text-primary-dark mb-4">
            Existing Jobs
          </h2>
          {jobs && jobs.length > 0 ? (
            <div className="space-y-3">
              {jobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  isSelected={selectedJobId === job.id} onClick={() => {
                    if (job.status === 'completed') {
                      navigate(`/table/${job.id}`);
                    } else if (job.status !== 'processing' && job.status !== 'failed') {
                      if (selectedJobId === job.id) {
                        // If already selected, scroll immediately
                        scrollToUpload();
                      } else {
                        // If different, state change triggers the useEffect scroll
                        setSelectedJobId(job.id);
                      }
                    }
                  }}
                  onDelete={handleDeleteJob}
                  onViewData={(jobId) => navigate(`/table/${jobId}`)}
                  onViewInsights={(jobId) => navigate(`/insights/${jobId}`)}
                />
              ))}
            </div>
          ) : (
            <p className="text-text-secondary-light dark:text-secondary text-center py-8">
              No jobs yet. Create your first job above.
            </p>
          )}
        </Card>

        {/* Upload Images */}
        {selectedJobId && (
          <div ref={uploadSectionRef}>
            <Card>
              <h2 className="text-2xl font-semibold text-text-primary-light dark:text-primary mb-4">
                Upload Images for {jobs.find(job => job.id === selectedJobId)?.name || 'Processing'}
              </h2>

              {/* Drag and Drop Area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                  ? 'border-brand-primary bg-brand-primary/10 dark:bg-brand-primary/5'
                  : 'border-border-light dark:border'
                  }`}
              >
                <svg
                  className="mx-auto h-12 w-12 text-text-muted-light dark:text-muted"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="mt-2 text-sm text-text-secondary-light dark:text-secondary">
                  Drag and drop images here, or click to select files
                </p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <div className="mt-4">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    Select Files
                  </Button>
                </div>
              </div>

              {files.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-semibold text-text-primary-light dark:text-primary mb-2">
                    Selected Files ({files.length})
                  </h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between gap-3 p-2 rounded bg-surface-hover dark:bg-surface-dark border border-border-light dark:border">
                        <span className="text-sm text-text-secondary-light dark:text-secondary flex-1 truncate">
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-danger hover:opacity-70 transition-opacity flex-shrink-0"
                          type="button"
                          aria-label="Remove file"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Configuration */}
              <div className="mt-6 space-y-4">
                <h3 className="font-semibold text-text-primary-light dark:text-primary">Configuration</h3>
                <Grid cols={2} gap={4}>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary-light dark:text-secondary mb-1">
                      Sample Size
                    </label>
                    <input
                      type="number"
                      value={sampleSize}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 3;
                        // Clamp sample size to be between 1 and number of files
                        const clampedValue = Math.min(Math.max(value, 1), files.length || value);
                        setSampleSize(clampedValue);
                      }}
                      min="1"
                      max={files.length || undefined}
                      className="w-full px-4 py-2 border border-border-light dark:border rounded-lg focus:outline-none bg-background-light dark:bg-surface-dark text-text-primary-light dark:text-primary"
                    />
                    {files.length > 0 && sampleSize > files.length && (
                      <p className="mt-1 text-xs text-danger dark:text-danger">
                        Sample size cannot exceed number of uploaded images ({files.length})
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary-light dark:text-secondary mb-1">
                      Max Images (Optional)
                    </label>
                    <input
                      type="number"
                      value={maxImages}
                      onChange={(e) => setMaxImages(e.target.value)}
                      placeholder="All"
                      min="1"
                      className="w-full px-4 py-2 border border-border-light dark:border rounded-lg focus:outline-none bg-background-light dark:bg-surface-dark text-text-primary-light dark:text-primary"
                    />
                  </div>
                </Grid>
              </div>

              {/* Process Button */}
              <div className="mt-6">
                <Button
                  onClick={handleProcessImages}
                  variant="primary"
                  fullWidth
                  disabled={files.length === 0 || processImagesMutation.isPending || sampleSize > files.length}
                >
                  {processImagesMutation.isPending ? 'Processing...' : 'Process Images'}
                </Button>
                {files.length > 0 && sampleSize > files.length && (
                  <p className="mt-2 text-sm text-danger dark:text-danger">
                    Please adjust the sample size to be {files.length} or less
                  </p>
                )}
              </div>

              {/* Progress Display */}
              {uploadProgress !== null && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-text-secondary-light dark:text-secondary">Upload Progress</span>
                    <span className="text-brand-primary dark:text-brand-primary">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-border-light dark:bg-border rounded-full h-2">
                    <div
                      className="bg-brand-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {processImagesMutation.isError && (
                <p className="mt-4 text-sm text-danger dark:text-danger">
                  Error processing images: {processImagesMutation.error.message}
                </p>
              )}
            </Card>
          </div>
        )}
      </Grid>
    </div>
  );
};

export default Home;
